import { Container, Graphics, Rectangle, utils } from 'pixi.js'
import { gsap } from 'gsap'
import { debugMode } from '@/scripts/config/settings'
import { openHiddenPlaceBox } from './index'
import { onPointerTap } from '@/scripts/helpers/events'
import Analytics, { ACTION_SECRET_HOVER } from '@/scripts/analytics'

export interface HiddenPlaceRect {
  x: number
  y: number
  w: number
  h: number
  id: string
}

export default class HiddenPlace extends Container {
  public readonly id: string
  private readonly hitRect: Rectangle
  private hitGraphics: Graphics

  constructor(place: HiddenPlaceRect) {
    super()
    this.id = place.id
    this.position.set(Math.round(place.x), Math.round(place.y))
    this.hitRect = new Rectangle(0, 0, place.w, place.h)

    // Interaction setup
    this.eventMode = 'static'
    this.cursor = 'default' // we'll toggle a container-level class instead
    this.zIndex = 200 // above base sprites, below UI overlays

    // Invisible interactive rect
    this.hitGraphics = new Graphics()
    this.hitGraphics
      .beginFill(0x000000, 0.001) // 0 alpha still blocks events poorly on some platforms; use 0.001
      .drawRect(0, 0, this.hitRect.width, this.hitRect.height)
      .endFill()
    this.addChild(this.hitGraphics)
    this.hitArea = this.hitRect

    if (debugMode) this.drawDebug()

    this.on('pointerover', this.onOver, this)
    this.on('pointerout', this.onOut, this)
    onPointerTap(this, this.onClick, this)
  }

  private onOver(): void {
    const canvasContainer = document.querySelector('.canvas-container')
    canvasContainer?.classList.add('cursor-magnifier')
    // subtle hover feedback for debug or to confirm hit area (no sprites here)
    if (debugMode) {
      gsap.to(this.hitGraphics, { duration: 0.15, alpha: 0.15 })
    }
    Analytics.sendCustomEvent(ACTION_SECRET_HOVER, this.id)
  }

  private onOut(): void {
    const canvasContainer = document.querySelector('.canvas-container')
    canvasContainer?.classList.remove('cursor-magnifier')
    if (debugMode) {
      gsap.to(this.hitGraphics, { duration: 0.15, alpha: 0.001 })
    }
  }

  private onClick(): void {
    // Open overlay with image: /images/page/sm_<id>.jpg
    openHiddenPlaceBox(this.id)
  }

  private drawDebug(): void {
    const g = new Graphics()
    g.lineStyle(1, utils.string2hex('0x00FF9D'), 0.8)
    g.drawRect(0, 0, this.hitRect.width, this.hitRect.height)
    g.endFill()
    this.addChild(g)
  }
}
